<template>
    <div v-if="isActualDevHost" class="dev-sidebar-wrapper" data-test="dev-sidebar-wrapper">
        <!-- Floating Left Trigger Handle -->
        <div
            class="dev-trigger-tab shadow-sm"
            :class="{ 'is-open': isOpen }"
            @click="toggleDrawer"
            data-test="btn-trigger-dev-sidebar"
            aria-label="Developer Environment Simulator"
            v-b-tooltip.hover.right="'Dev Simulator & Layout Inspector (Ctrl+Shift+D)'"
        >
            <div class="d-flex align-items-center justify-content-center">
                <i class="bi bi-terminal-fill dev-trigger-icon" :style="{ color: triggerIconColor }"></i>
                <span
                    v-if="hasActiveOverrides"
                    class="badge badge-warning ml-1 font-weight-bold extra-small dev-sim-pill"
                    data-test="badge-dev-sim-active"
                >
                    {{ activeOverrideBadgeText }}
                </span>
            </div>
        </div>

        <!-- Backdrop Overlay -->
        <div v-if="isOpen" class="dev-drawer-backdrop" @click="isOpen = false"></div>

        <!-- Slide-out Drawer Panel -->
        <transition name="slide-drawer">
            <div v-if="isOpen" class="dev-drawer-panel shadow-lg" data-test="panel-dev-sidebar">
                <!-- Header -->
                <div class="dev-drawer-header d-flex justify-content-between align-items-center p-3 border-bottom drawer-border">
                    <div class="d-flex align-items-center">
                        <i class="bi bi-terminal-fill mr-2" :style="{ color: triggerIconColor, fontSize: '1.25rem' }"></i>
                        <div>
                            <h6 class="font-weight-bold mb-0 drawer-heading-text">Dev Simulator &amp; Inspector</h6>
                            <span class="extra-small text-muted">Simulate Release Layouts &amp; Gates</span>
                        </div>
                    </div>
                    <div class="d-flex align-items-center">
                        <span
                            class="badge font-weight-bold mr-2 px-2 py-1 text-uppercase"
                            :class="headerBadgeClass"
                        >
                            {{ effectiveEnvDisplay }}
                        </span>
                        <button
                            type="button"
                            class="btn btn-sm btn-link text-muted p-0"
                            @click="isOpen = false"
                            title="Close Panel (Esc)"
                            data-test="btn-close-dev-sidebar"
                        >
                            <i class="bi bi-x-lg" style="font-size: 1.1rem;"></i>
                        </button>
                    </div>
                </div>

                <!-- Drawer Body -->
                <div class="dev-drawer-body p-3">
                    <!-- SECTION 1: BACKEND INSTANCE MODE -->
                    <div class="mb-4">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                            <span class="font-weight-bold small text-uppercase drawer-section-title">
                                <i class="bi bi-hdd-network mr-1"></i>1. Backend Instance Mode
                            </span>
                            <button
                                type="button"
                                class="btn btn-xs btn-outline-secondary py-0 px-2 extra-small"
                                :disabled="!hasActiveOverrides"
                                @click="resetAllOverrides"
                                data-test="btn-sim-reset-all"
                                title="Reset backend instance mode to Local Dev default"
                            >
                                <i class="bi bi-arrow-counterclockwise mr-1"></i>Reset
                            </button>
                        </div>

                        <!-- 3-Segment Backend Instance Mode Buttons -->
                        <div class="sim-segmented-group mb-2">
                            <button
                                type="button"
                                class="sim-seg-btn"
                                :class="{ active: !simEnv || simEnv === 'development' }"
                                @click="setSimulatedEnv('development')"
                                data-test="btn-sim-env-dev"
                                title="Local Dev Instance (In-Dev features unlocked, standard workspace plan)"
                            >
                                <i class="bi bi-code-slash mr-1"></i>Local Dev
                            </button>
                            <button
                                type="button"
                                class="sim-seg-btn"
                                :class="{ active: simEnv === 'beta' }"
                                @click="setSimulatedEnv('beta')"
                                data-test="btn-sim-env-beta"
                                title="Live Beta Instance (All users are Beta users with Pro + 1 Team batch unlocked)"
                            >
                                <i class="bi bi-stars mr-1"></i>Live Beta
                            </button>
                            <button
                                type="button"
                                class="sim-seg-btn"
                                :class="{ active: simEnv === 'production' }"
                                @click="setSimulatedEnv('production')"
                                data-test="btn-sim-env-prod"
                                title="Live Production Instance (Standard Free/Pro/Team tiers & quotas enforced)"
                            >
                                <i class="bi bi-shield-check mr-1"></i>Live Prod
                            </button>
                        </div>

                        <!-- Status Summary Card -->
                        <div class="sim-status-card p-2 rounded mb-2 border">
                            <div class="d-flex justify-content-between align-items-center mb-1 small">
                                <span class="text-muted">Backend Instance:</span>
                                <span class="font-weight-bold text-uppercase font-monospace" data-test="status-effective-env">
                                    {{ currentEnvironment }}
                                </span>
                            </div>
                            <div class="d-flex justify-content-between align-items-center mb-1 small">
                                <span class="text-muted">Beta Instance Mark:</span>
                                <span
                                    class="font-weight-bold font-monospace"
                                    :class="isBetaUser ? 'text-warning' : 'text-muted'"
                                    data-test="status-beta-instance-mark"
                                >
                                    {{ isBetaUser ? 'ACTIVE (All Users = Beta)' : 'OFF (Standard Tiers)' }}
                                </span>
                            </div>
                            <div class="d-flex justify-content-between align-items-center mb-1 small">
                                <span class="text-muted">Effective Plan:</span>
                                <span class="font-weight-bold font-monospace text-primary" data-test="status-effective-plan">
                                    {{ `${planTierLabel} (${planTier})${isBetaUser ? ' — Free in Beta' : ''}` }}
                                </span>
                            </div>
                            <div class="d-flex justify-content-between align-items-center small">
                                <span class="text-muted">In-Dev Features (isDevEnv):</span>
                                <span
                                    class="font-weight-bold"
                                    :class="isDevEnv ? 'text-success' : 'text-warning'"
                                    data-test="status-in-dev-features"
                                >
                                    {{ isDevEnv ? 'UNLOCKED' : 'HIDDEN ("Coming Next")' }}
                                </span>
                            </div>
                        </div>

                        <div class="d-flex gap-2">
                            <button
                                type="button"
                                class="btn btn-xs btn-outline-info flex-fill py-1 extra-small"
                                @click="openBetaWelcomeModal"
                                data-test="btn-sim-open-beta-welcome"
                                title="Open Beta Welcome / Consent Modal"
                            >
                                <i class="bi bi-stars mr-1"></i>Beta Welcome Modal
                            </button>
                            <button
                                type="button"
                                class="btn btn-xs btn-outline-warning flex-fill py-1 extra-small"
                                :disabled="!isAuthenticated"
                                @click="openBetaChangelogDrawer"
                                data-test="btn-sim-open-beta-drawer"
                                :title="isAuthenticated ? 'Open Beta Changelog Drawer' : 'Sign in to open the in-app Beta Changelog Drawer'"
                            >
                                <i class="bi bi-journal-text mr-1"></i>Beta Changelog
                            </button>
                        </div>
                    </div>

                    <!-- SECTION 2: IN-DEVELOPMENT FEATURE GATES & REGRESSION CHECKLIST -->
                    <div>
                        <div class="d-flex justify-content-between align-items-center mb-2">
                            <span class="font-weight-bold small text-uppercase drawer-section-title">
                                <i class="bi bi-clipboard-check mr-1"></i>2. Feature Regression Jump
                            </span>
                        </div>
                        <div class="sim-checklist-wrapper d-flex flex-column gap-2">
                            <!-- Feature 1: Team & Capacity Management -->
                            <div class="sim-checklist-item p-2 rounded border">
                                <div class="d-flex justify-content-between align-items-center mb-1">
                                    <span class="font-weight-bold small">Team &amp; Capacity</span>
                                    <span
                                        class="badge extra-small font-weight-bold"
                                        :class="isDevEnv ? 'badge-success' : 'badge-warning'"
                                    >
                                        {{ isDevEnv ? 'Unlocked (Full UI)' : 'Coming Next' }}
                                    </span>
                                </div>
                                <div class="extra-small text-muted mb-2">
                                    Workload balance, timesheet audit, absence schedule (/team).
                                </div>
                                <button
                                    type="button"
                                    class="btn btn-xs btn-outline-primary w-100 py-1 extra-small"
                                    @click="jumpTo('/team')"
                                    data-test="btn-jump-team-page"
                                >
                                    <i class="bi bi-arrow-right-circle mr-1"></i>Jump to Team Page
                                </button>
                            </div>

                            <!-- Feature 2: Config Team Workspaces & Squads -->
                            <div class="sim-checklist-item p-2 rounded border">
                                <div class="d-flex justify-content-between align-items-center mb-1">
                                    <span class="font-weight-bold small">Config: Team Workspaces</span>
                                    <span
                                        class="badge extra-small font-weight-bold"
                                        :class="isDevEnv ? 'badge-success' : 'badge-warning'"
                                    >
                                        {{ isDevEnv ? 'Unlocked (CRUD)' : 'Coming Next' }}
                                    </span>
                                </div>
                                <div class="extra-small text-muted mb-2">
                                    Multi-seat team workspace administration &amp; squads (/config?section=teams).
                                </div>
                                <button
                                    type="button"
                                    class="btn btn-xs btn-outline-primary w-100 py-1 extra-small"
                                    @click="jumpTo('/config?section=teams')"
                                    data-test="btn-jump-config-teams"
                                >
                                    <i class="bi bi-arrow-right-circle mr-1"></i>Jump to Config Teams
                                </button>
                            </div>

                            <!-- Feature 3: Plans 5-Seat Batch Pricing -->
                            <div class="sim-checklist-item p-2 rounded border">
                                <div class="d-flex justify-content-between align-items-center mb-1">
                                    <span class="font-weight-bold small">Plans: 5-Seat Batch</span>
                                    <span
                                        class="badge extra-small font-weight-bold"
                                        :class="isDevEnv ? 'badge-success' : 'badge-warning'"
                                    >
                                        {{ isDevEnv ? 'Unlocked (5-Seat)' : 'Coming Next' }}
                                    </span>
                                </div>
                                <div class="extra-small text-muted mb-2">
                                    Multi-seat team pricing table with seat multiplier selection (/plans).
                                </div>
                                <button
                                    type="button"
                                    class="btn btn-xs btn-outline-primary w-100 py-1 extra-small"
                                    @click="jumpTo('/plans')"
                                    data-test="btn-jump-plans"
                                >
                                    <i class="bi bi-arrow-right-circle mr-1"></i>Jump to Plans
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
    import mixinAccount from '../mixinAccount';
    import eventBus from '../event-bus';
    import { setEnvironmentFavicons } from '../envHelper';

    export default {
        name: 'DevSystemSidebar',
        mixins: [mixinAccount],
        data() {
            return {
                isOpen: false
            };
        },
        computed: {
            simEnv() {
                return this.$store.state.devSimulator?.envOverride || null;
            },
            hasActiveOverrides() {
                return !!this.simEnv && this.simEnv !== 'development';
            },
            effectiveEnvDisplay() {
                if (this.currentEnvironment === 'production') return 'PROD';
                if (this.currentEnvironment === 'beta') return 'BETA';
                return 'DEV';
            },
            headerBadgeClass() {
                if (this.currentEnvironment === 'production') return 'badge-prod-sim';
                if (this.currentEnvironment === 'beta') return 'badge-beta-sim';
                return 'badge-dev-sim';
            },
            triggerIconColor() {
                if (this.currentEnvironment === 'production') return '#d11cf4';
                if (this.currentEnvironment === 'beta') return '#f59e0b';
                return '#08eb26';
            },
            activeOverrideBadgeText() {
                if (this.simEnv === 'beta') return 'SIM: BETA';
                if (this.simEnv === 'production') return 'SIM: PROD';
                return 'DEV';
            },
            planTierLabel() {
                const tier = this.planTier;
                if (tier === 0) return 'Free';
                if (tier === 1) return 'Solo/Pro';
                if (tier === 2) return 'Team';
                if (tier === 3) return 'Business';
                if (tier === 4) return 'Enterprise';
                return 'Free';
            }
        },
        mounted() {
            window.addEventListener('keydown', this.handleKeyDown);
            if (this.isActualDevHost && this.$http) {
                this.$http.post('/AccountConfig/SetDevInstanceMode', {
                    instanceMode: this.currentEnvironment || 'development'
                }).catch(() => {});
            }
        },
        beforeUnmount() {
            window.removeEventListener('keydown', this.handleKeyDown);
        },
        methods: {
            toggleDrawer() {
                this.isOpen = !this.isOpen;
            },
            handleKeyDown(e) {
                if (e.ctrlKey && e.shiftKey && (e.code === 'KeyD' || e.key === 'D')) {
                    e.preventDefault();
                    this.toggleDrawer();
                } else if (e.key === 'Escape' && this.isOpen) {
                    this.isOpen = false;
                }
            },
            async setSimulatedEnv(env) {
                const normalized = (env === 'beta' || env === 'production') ? env : 'development';
                const target = normalized === 'development' ? null : normalized;
                this.$store.commit('setDevSimulatorEnv', target);
                this.$store.commit('setBetaUser', normalized === 'beta');
                setEnvironmentFavicons(this.currentEnvironment);
                if (normalized === 'beta') {
                    this.$store.commit('setAcknowledgedBetaWelcome', true);
                }
                if (this.$http) {
                    try {
                        await this.$http.post('/AccountConfig/SetDevInstanceMode', {
                            instanceMode: normalized
                        });
                    } catch (e) {
                        // ignore in offline/unit contexts
                    }
                }
                if (this.isAuthenticated) {
                    await this.initSessionConfigAsync();
                }
            },
            async resetAllOverrides() {
                await this.setSimulatedEnv('development');
            },
            openBetaWelcomeModal() {
                eventBus.$emit('show-beta-welcome-modal');
            },
            openBetaChangelogDrawer() {
                if (!this.isAuthenticated) return;
                this.$store.commit('setAcknowledgedBetaWelcome', true);
                this.$store.commit('setBetaWelcomeOpen', false);
                this.$nextTick(() => {
                    eventBus.$emit('open-beta-info');
                });
            },
            jumpTo(path) {
                if (this.$route.fullPath !== path) {
                    this.$router.push(path).catch(() => {});
                }
                this.isOpen = false;
            }
        }
    };
</script>

<style scoped>
/* Theme tokens */
.dev-sidebar-wrapper,
.dev-drawer-panel,
.dev-trigger-tab {
    --drawer-bg: #ffffff;
    --drawer-header-bg: #f8fafc;
    --drawer-nav-bg: #f1f5f9;
    --drawer-nav-segment-bg: #e2e8f0;
    --drawer-nav-btn-color: #475569;
    --drawer-nav-btn-hover-color: #0f172a;
    --drawer-nav-btn-hover-bg: #ffffff;
    --drawer-text: #334155;
    --drawer-text-heading: #0f172a;
    --drawer-text-muted: #64748b;
    --drawer-border: #e2e8f0;
    --drawer-card-bg: #f8fafc;
    --drawer-card-hover-bg: #f1f5f9;
    --drawer-card-border: #e2e8f0;
    --drawer-card-border-hover: #cbd5e1;
    --drawer-trigger-bg: #ffffff;
    --drawer-trigger-color: #334155;
    --drawer-trigger-border: #cbd5e1;
    --drawer-trigger-hover-bg: #f8fafc;
    --drawer-scrollbar-thumb: #cbd5e1;
    --drawer-scrollbar-track: rgba(0, 0, 0, 0.04);
}

[data-theme="dark"] .dev-sidebar-wrapper,
[data-theme="dark"] .dev-drawer-panel,
[data-theme="dark"] .dev-trigger-tab {
    color-scheme: dark;
    --drawer-bg: #0f172a;
    --drawer-header-bg: #1e293b;
    --drawer-nav-bg: #162032;
    --drawer-nav-segment-bg: #0f172a;
    --drawer-nav-btn-color: #94a3b8;
    --drawer-nav-btn-hover-color: #f8fafc;
    --drawer-nav-btn-hover-bg: rgba(255, 255, 255, 0.08);
    --drawer-text: #e2e8f0;
    --drawer-text-heading: #ffffff;
    --drawer-text-muted: #94a3b8;
    --drawer-border: #334155;
    --drawer-card-bg: #1e293b;
    --drawer-card-hover-bg: #233147;
    --drawer-card-border: #334155;
    --drawer-card-border-hover: #475569;
    --drawer-trigger-bg: #1e293b;
    --drawer-trigger-color: #f8fafc;
    --drawer-trigger-border: #334155;
    --drawer-trigger-hover-bg: #233147;
    --drawer-scrollbar-thumb: #475569;
    --drawer-scrollbar-track: rgba(255, 255, 255, 0.04);
}

/* Floating left trigger handle */
.dev-trigger-tab {
    position: fixed;
    top: 112px;
    left: 0;
    z-index: 1045;
    background-color: var(--drawer-trigger-bg) !important;
    color: var(--drawer-trigger-color) !important;
    border: 1px solid var(--drawer-trigger-border) !important;
    border-left: none !important;
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
    padding: 6px 8px;
    font-size: 0.75rem;
    font-weight: 700;
    cursor: pointer;
    user-select: none;
    transition: all 0.2s ease;
    box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.15);
    opacity: 0.85;
}

.dev-trigger-tab:hover {
    opacity: 1;
    transform: translateX(2px);
    background-color: var(--drawer-trigger-hover-bg) !important;
    color: var(--drawer-text-heading) !important;
    border-color: var(--drawer-card-border-hover) !important;
}

.dev-trigger-tab.is-open {
    opacity: 1;
    background-color: var(--drawer-trigger-hover-bg) !important;
}

.dev-trigger-icon {
    font-size: 0.85rem;
}

.dev-sim-pill {
    font-size: 0.65rem;
    padding: 2px 4px;
    background-color: #f59e0b !important;
    color: #ffffff !important;
    border-radius: 4px;
}

/* Backdrop */
.dev-drawer-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1050;
    backdrop-filter: blur(2px);
}

/* Drawer panel */
.dev-drawer-panel {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: 420px;
    max-width: 94vw;
    background-color: var(--drawer-bg) !important;
    color: var(--drawer-text) !important;
    border-right: 1px solid var(--drawer-border) !important;
    z-index: 1060;
    display: flex;
    flex-direction: column;
}

.dev-drawer-header {
    background-color: var(--drawer-header-bg) !important;
    border-bottom: 1px solid var(--drawer-border) !important;
    flex-shrink: 0;
}

.drawer-heading-text {
    color: var(--drawer-text-heading) !important;
}

.drawer-section-title {
    color: var(--drawer-text-muted);
    letter-spacing: 0.5px;
}

.dev-drawer-body {
    flex: 1 1 auto;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: var(--drawer-scrollbar-thumb) var(--drawer-scrollbar-track);
}

.dev-drawer-body::-webkit-scrollbar {
    width: 6px;
}

.dev-drawer-body::-webkit-scrollbar-track {
    background: var(--drawer-scrollbar-track);
}

.dev-drawer-body::-webkit-scrollbar-thumb {
    background-color: var(--drawer-scrollbar-thumb);
    border-radius: 3px;
}

/* Segmented button group */
.sim-segmented-group {
    display: flex;
    background-color: var(--drawer-nav-segment-bg);
    border: 1px solid var(--drawer-border);
    border-radius: 6px;
    padding: 3px;
    gap: 3px;
}

.sim-seg-btn {
    flex: 1;
    background: transparent;
    border: none;
    color: var(--drawer-nav-btn-color);
    font-size: 0.75rem;
    font-weight: 600;
    padding: 5px 8px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s ease-in-out;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
}

.sim-seg-btn:hover {
    color: var(--drawer-nav-btn-hover-color);
    background-color: var(--drawer-nav-btn-hover-bg);
}

.sim-seg-btn.active {
    background-color: #2563eb !important;
    color: #ffffff !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}

/* Cards & items */
.sim-status-card,
.sim-checklist-item {
    background-color: var(--drawer-card-bg);
    border-color: var(--drawer-card-border) !important;
    color: var(--drawer-text);
}

.sim-checklist-item {
    transition: border-color 0.15s ease, background-color 0.15s ease;
}

.sim-checklist-item:hover {
    background-color: var(--drawer-card-hover-bg);
    border-color: var(--drawer-card-border-hover) !important;
}

/* Badges */
.badge-dev-sim {
    background-color: #10b981 !important;
    color: #ffffff !important;
}

.badge-beta-sim {
    background-color: #f59e0b !important;
    color: #ffffff !important;
}

.badge-prod-sim {
    background-color: #9333ea !important;
    color: #ffffff !important;
}

/* Slide Transition */
.slide-drawer-enter-active,
.slide-drawer-leave-active {
    transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-drawer-enter,
.slide-drawer-leave-to {
    transform: translateX(-100%);
}

/* Responsive Full-Screen on Mobile Phone (<576px) */
@media (max-width: 575.98px) {
    .dev-trigger-tab {
        display: none !important;
    }

    .dev-drawer-panel {
        width: 100% !important;
        width: 100vw !important;
        max-width: 100% !important;
        max-width: 100vw !important;
        height: 100% !important;
        height: 100vh !important;
        height: 100dvh !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        border-radius: 0 !important;
        border: none !important;
        z-index: 1060 !important;
    }

    .dev-drawer-backdrop {
        z-index: 1055 !important;
    }
}
</style>
